/**
 * Handing the plan view back from one onboarding step to the next map that opens
 * (`spec-onboarding-persistent-scene-editor-pose`, R5).
 *
 * Two values, held outside React and any store: only one plan map is live at a time, and a pan must
 * not wake every store subscriber.
 *
 * - **The live pose:** the camera the live plan map (the editor, or the Observers stage plan) last
 *   jumped to, where its pane's centre is on the viewport, and the camera it first opened at.
 *   Recorded on every camera applied, cleared when that map unmounts.
 * - **The request:** what the last section change handed back. `OnboardingPage`'s one section
 *   setter calls `handBack()` before every change; the next plan map to mount takes it, once.
 *
 * Nothing here is stored, logged or sent anywhere (spec, Access and Data Exposure).
 */
import type { PlanCamera } from './planCamera';

/** A plan map's camera with where it was drawn: the request a step hands back. */
export interface HandedPose extends PlanCamera {
  twinBid: string;
  /** The pane's centre, in viewport CSS pixels, when the camera was applied. */
  at: { x: number; y: number };
}

interface LivePose extends HandedPose {
  /** The camera the map first opened at. */
  opening: PlanCamera;
  /**
   * The map opened at a view handed back to it, not at its own fit. That view is still the person's,
   * moved or not, so it is handed on (criterion 4: Floor Plan → Camera Feeds → Observers keeps it).
   */
  carried: boolean;
  /**
   * Position on map's pose: handed on only across a handed-over step change
   * (`spec-onboarding-persistent-scene-handover`, R4.2). To every other change it is as if no map
   * were live.
   */
  handoverOnly: boolean;
  /**
   * The map that recorded it, where it named itself: two maps that share the onboarding stage
   * (Position on map, the washed plan) overlap while one fades out, and each clears only its own
   * (`spec-onboarding-persistent-scene-wash`, review).
   */
  owner: object | null;
}

/**
 * What a section change handed back. `carryOnly`: handed on only because the change was handed over
 * (an unmoved, fitted view, or Position on map's), so the map that opens at it is not carried by it
 * (handover R2.2: every other step change behaves as before, and fits).
 */
interface HandedRequest extends HandedPose {
  carryOnly: boolean;
  /** Handed on by the live viewer's Edit (`spec-onboarding-persistent-scene-edit-flight`, R6.3). */
  fromLive?: boolean;
}

let live: LivePose | null = null;
let request: HandedRequest | null = null;
/** The request the mounting map last took was `carryOnly`. */
let takenCarryOnly = false;
/**
 * The request the mounting map took, until it records a camera of its own. A join made before that
 * map has drawn hands this on, so the plan does not jump when the held picture fades (handover R4.1:
 * Floor Plan, then Camera Feeds, then Observers before Camera Feeds' map has loaded).
 */
let taken: HandedRequest | null = null;

/** A moved view: more than these from the camera it opened at (R5.2). */
const ZOOM_TOLERANCE = 0.01;
const BEARING_TOLERANCE_DEG = 0.1;
const CENTRE_TOLERANCE_PX = 1;

const finiteCamera = (c: PlanCamera) =>
  Number.isFinite(c.center[0]) &&
  Number.isFinite(c.center[1]) &&
  Number.isFinite(c.zoom) &&
  Number.isFinite(c.bearing);

/**
 * A longitude and latitude in Web Mercator units (0 to 1), as Mapbox's `MercatorCoordinate` computes
 * them. Written out so the page's own chunk, which calls `handBack`, never loads mapbox-gl.
 */
const mercator = ([lng, lat]: [number, number]) => ({
  x: (180 + lng) / 360,
  y: (180 - (180 / Math.PI) * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360))) / 360,
});

const shortestTurn = (deg: number) => ((((deg + 180) % 360) + 360) % 360) - 180;

/** CSS pixels per Mercator unit at zoom 0: Mapbox's tile size (`planCamera`'s, not imported). */
const TILE_SIZE = 512;

/** Are two cameras within R5.2's tolerances of each other: zoom, bearing, and centre at `a`'s zoom? */
const near = (a: PlanCamera, b: PlanCamera): boolean => {
  if (Math.abs(a.zoom - b.zoom) > ZOOM_TOLERANCE) return false;
  if (Math.abs(shortestTurn(a.bearing - b.bearing)) > BEARING_TOLERANCE_DEG) return false;
  const p = mercator(a.center);
  const q = mercator(b.center);
  return Math.hypot(p.x - q.x, p.y - q.y) * TILE_SIZE * 2 ** a.zoom <= CENTRE_TOLERANCE_PX;
};

/** Has the view moved from where it opened, by more than R5.2's tolerances? */
const moved = (pose: LivePose): boolean => !near(pose, pose.opening);

/** What the map knows about the camera it applied, beyond the camera. */
export interface LivePoseNote {
  /**
   * The view is still the map's own opening, unmoved: re-applied for a settled pane, say. It becomes
   * the opening, so only the person's move counts (R5.2).
   */
  opening?: boolean;
  /** The map applied a view handed back to it, clamped or not: it is carried (criterion 4). */
  handed?: boolean;
  /** Position on map's map: its pose goes on only across a handed-over change (handover R4.2). */
  handoverOnly?: boolean;
  /** The recording map, so its unmount clears only a pose it recorded (`clearLivePose`'s `owner`). */
  owner?: object;
}

/**
 * R5.1: the live map applied `pose`. A pose with a blank twin or a non-finite number is not recorded,
 * and the previous one stands. The first pose since the last `clearLivePose` is the opening.
 */
export function recordLivePose(pose: HandedPose, note: LivePoseNote = {}): void {
  if (pose.twinBid.trim() === '') return;
  if (!finiteCamera(pose) || !Number.isFinite(pose.at.x) || !Number.isFinite(pose.at.y)) return;
  const camera: PlanCamera = { center: pose.center, zoom: pose.zoom, bearing: pose.bearing };
  const same = live && live.twinBid === pose.twinBid ? live : null;
  // The pane moved or resized under a view nobody moved: the map re-fitted or re-placed it, so this
  // camera is where it now opens (R5.2: only the person's move counts).
  const reseated =
    !!note.opening ||
    (!!same &&
      Math.hypot(pose.at.x - same.at.x, pose.at.y - same.at.y) > CENTRE_TOLERANCE_PX &&
      !moved(same));
  const opening = same && !reseated ? same.opening : camera;
  // Carried once the map applied a handed view, not its own fit (a Build floors mount, which takes
  // the request and fits). A view handed only across a handover does not carry: unmoved, it is as if
  // the map had fitted it.
  const carried = (same?.carried ?? false) || (!!note.handed && !takenCarryOnly);
  taken = null;
  live = {
    twinBid: pose.twinBid,
    center: [pose.center[0], pose.center[1]],
    zoom: pose.zoom,
    bearing: pose.bearing,
    at: { x: pose.at.x, y: pose.at.y },
    opening,
    carried,
    handoverOnly: !!note.handoverOnly,
    owner: note.owner ?? null,
  };
}

/**
 * R5.1: the live map unmounted. `handoverOnly`: Position on map's map clears only its own pose, as
 * the editor arriving in the same commit may already have recorded its own (handover R4.2). `owner`:
 * clears only a pose that map recorded, so a stage map fading out never clears the washed plan's,
 * recorded while it faded (`spec-onboarding-persistent-scene-wash`, review).
 * `withhold`: the map is on screen but has no plan view to hand on (tilted), so a view it took is
 * not handed on either and the next map fits.
 */
export function clearLivePose({
  handoverOnly = false,
  withhold = false,
  owner,
}: { handoverOnly?: boolean; withhold?: boolean; owner?: object } = {}): void {
  if (handoverOnly && !live?.handoverOnly) return;
  if (owner && live && live.owner !== owner) return;
  live = null;
  if (withhold) taken = null;
}

/**
 * R5.2: a section is about to change. A moved live view becomes the request, as does one that opened
 * at a handed-back view; a fitted, unmoved one clears it, so the next map fits. With no live map the
 * request waits (R5.3: Observers → Composite measures → Position on map). Only the last map left
 * counts.
 *
 * `carry` (`spec-onboarding-persistent-scene-handover`, R4.1): the change is handed over, so the
 * live view is handed on moved or not, Position on map's included. Without it, Position on map's
 * pose counts as no live map (R4.2), and the rule above stands unchanged.
 */
export function handBack({ carry = false }: { carry?: boolean } = {}): void {
  const untaken = taken;
  taken = null;
  if (!live) {
    // The map just mounted took a view and has drawn nothing yet: a join hands that view on.
    if (carry && untaken) request = untaken;
    return;
  }
  if (live.handoverOnly && !carry) return;
  const ownRight = !live.handoverOnly && (live.carried || moved(live));
  if (carry || ownRight) {
    const { twinBid, center, zoom, bearing, at } = live;
    request = { twinBid, center, zoom, bearing, at, carryOnly: !ownRight };
    return;
  }
  request = null;
}

/**
 * The live viewer's Edit landed (`spec-onboarding-persistent-scene-edit-flight`, R6.3): its camera
 * is handed on, once, to the plan map onboarding opens next, so Floor Plan opens with the plan where
 * the held picture shows it. Join-only, as a handed-over step change's unmoved view is: Floor Plan is
 * not carried by it, so its next change that is not a join fits as usual. A pose with a blank twin or
 * a non-finite number hands nothing on.
 */
export function handOnLivePose(pose: HandedPose): void {
  if (pose.twinBid.trim() === '') return;
  if (!finiteCamera(pose) || !Number.isFinite(pose.at.x) || !Number.isFinite(pose.at.y)) return;
  const { twinBid, center, zoom, bearing, at } = pose;
  request = {
    twinBid,
    center: [center[0], center[1]],
    zoom,
    bearing,
    at: { x: at.x, y: at.y },
    carryOnly: true,
    fromLive: true,
  };
}

/** Is the live viewer's Edit hand-on still waiting for `twinBid`'s first plan map? */
export const liveHandOnWaiting = (twinBid: string | null | undefined): boolean =>
  !!twinBid && !!request && !!request.fromLive && request.twinBid === twinBid;

/**
 * R5.3: a plan map is mounting for `twinBid`. Returns the request and clears it; a request for
 * another twin is cleared and ignored.
 */
export function takeHandback(twinBid: string): HandedPose | null {
  const waiting = request;
  request = null;
  if (!waiting || waiting.twinBid !== twinBid) {
    takenCarryOnly = false;
    taken = null;
    return null;
  }
  const { carryOnly, fromLive: _fromLive, ...pose } = waiting;
  takenCarryOnly = carryOnly;
  taken = waiting;
  return pose;
}

/** R5.4: the page unmounted, or its twin changed. */
export function clearHandback(): void {
  request = null;
  live = null;
  takenCarryOnly = false;
  taken = null;
}

/** For tests: forget everything. */
export const resetPlanPoseHandbackForTests = clearHandback;
